import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { BiEdit, BiSearch } from "react-icons/bi";
import { useClinicListAPI } from "@/app/hooks/api_hooks/org/useClinicListAPI";
import clsx from "clsx";

const ClinicListComponent = () => {
  const { NO_PWL_RESULTS } = useImageConstants();
  const { execute: fetchClinicListAPI, clinicList } = useClinicListAPI();
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchClinicListAPI();
  }, [fetchClinicListAPI]);

  const filteredClinicList = useMemo(() => {
    if (!searchQuery) return clinicList;
    return clinicList?.filter((item: any) =>
      item?.clinic_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item?.branch_id?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [clinicList, searchQuery]);

  const handelProductEdit = (id: string) => {
    navigate(`/app/clinic-lab-report/lab-partner-clinic-mapping?clinic_id=${id}&is_clinic_mapping=true`);
  };

  const isLabPartnerListInProgess = useLoader("isLabPartnerListInProgress");

  return (
    <div className="bg-[#f8f9fa] min-h-full p-4">
      <div className=" mx-auto space-y-4">
        {/* Header Section */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-sm font-semibold text-gray-900 tracking-tight">Clinic Lab Partner Mapping</h1>
              <p className="text-[11px] text-gray-500 mt-0.5">Manage and map lab partners to your clinics</p>
            </div>
            
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <BiSearch className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search clinics by name or ID..."
                className="block w-full h-9 pl-9 pr-3 bg-gray-50 border border-gray-200 rounded-lg text-xs placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
          {isLabPartnerListInProgess ? (
            <div className="p-4">
              <TableSkeleton />
            </div>
          ) : filteredClinicList?.length === 0 ? (
            <div className="py-12">
              <NoDataFoundComponent 
                imageURL={NO_PWL_RESULTS} 
                message={searchQuery ? `No clinics found matching "${searchQuery}"` : "No Clinics Found"} 
              />
            </div>
          ) : (
            <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
              <div className="scroll-inner !h-auto max-h-[calc(100vh-220px)]">
                <div className="w-full">
                  <div className="inline-block min-w-full align-middle">
                    <table className="w-full min-w-[520px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                      <thead className="bg-[#F5F5F5] text-[#1F1F23] sticky top-0 z-10 font-normal">
                        <tr>
                          {["#", "Clinic Name", "Actions"].map((col) => (
                            <th
                              key={col}
                              scope="col"
                              className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize bg-[#F5F5F5]"
                            >
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="overflow-hidden rounded-xl w-full">
                        {filteredClinicList?.map((item: any, index: number) => {
                          const isFirst = index === 0;
                          const isLast = index === filteredClinicList?.length - 1;
                          const isSingle = filteredClinicList?.length === 1;

                          return (
                            <tr
                              key={item?.branch_id}
                              className={clsx(
                                "group bg-white transition",
                                isSingle && "rounded-xl",
                                isFirst && !isSingle && "rounded-t-xl",
                                isLast && !isSingle && "rounded-b-xl",
                              )}
                            >
                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]",
                                  isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                  isFirst && !isSingle && "rounded-tl-2xl",
                                  isLast && !isSingle && "rounded-bl-2xl",
                                )}
                              >
                                {(index + 1).toString().padStart(2, "0")}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                                {item?.clinic_name}
                              </td>

                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                  isSingle && "rounded-tr-2xl rounded-br-2xl",
                                  isFirst && !isSingle && "rounded-tr-2xl",
                                  isLast && !isSingle && "rounded-br-2xl",
                                )}
                              >
                                <button
                                  onClick={() => handelProductEdit(item?.id)}
                                  className="inline-flex h-7 items-center gap-1.5 rounded-xl bg-slate-100 px-3 text-xs font-medium text-[#1F1F23] transition hover:bg-slate-200"
                                >
                                  <BiEdit className="h-4 w-4" />
                                  <span>Assign Partner</span>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-between items-center px-1 text-[10px] text-gray-400">
          <p>Showing {filteredClinicList?.length || 0} results</p>
          <p>Last updated just now</p>
        </div>
      </div>
    </div>
  );
};

export default ClinicListComponent;
